import { createClient } from "@smfa/utils";
const baseUrl = process.env?.API_URL || process.env?.REACT_APP_BACKEND_URL;

const client = createClient({ baseUrl, isAdminSite: true });

// Global configuration constants
export const REQUEST_TIMEOUT = 15000; // 15 seconds timeout
const MIN_REQUEST_INTERVAL = 1000; // 1 second minimum between requests
let lastRequestTime = 0;

/**
 * Normalize the current and legacy list response contracts used by the
 * banner endpoint. A successful empty legacy response used status=false and
 * a message string instead of an array; it is still a valid empty list.
 */
export const normalizeSliderListResponse = (response) => {
  const payload = response?.data;
  const pagination = payload?.pagination;

  if (
    !payload ||
    typeof payload !== "object" ||
    !pagination ||
    typeof pagination !== "object"
  ) {
    return null;
  }

  const hasRows = Array.isArray(payload.data);
  const totalCountValue = pagination.totalCount ?? pagination.total_records;
  const hasZeroRows =
    totalCountValue == null || Number(totalCountValue) === 0;
  const isSuccessfulList = payload.status === true && hasRows;
  const isLegacyEmptyList =
    payload.status === false &&
    hasZeroRows &&
    (typeof payload.data === "string" || payload.data == null);

  if (!isSuccessfulList && !isLegacyEmptyList) {
    return null;
  }

  const totalCount = Number(totalCountValue ?? 0);
  const totalPage = Number(
    pagination.totalPage ?? pagination.total_page ?? 0,
  );

  return {
    data: hasRows ? payload.data : [],
    totalCount: Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : 0,
    totalPage: Number.isFinite(totalPage) && totalPage >= 0 ? totalPage : 0,
  };
};

/**
 * Enhanced request handler with rate limiting and error handling
 * @param {Function} requestFn - Function that performs the actual API request
 * @returns {Promise} - Response from API
 */
const enhancedRequest = async (requestFn) => {
  try {
    // Rate limiting implementation
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;

    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      log.debug(
        `Rate limiting: waiting ${MIN_REQUEST_INTERVAL - timeSinceLastRequest}ms`,
      );
      await new Promise((resolve) =>
        setTimeout(resolve, MIN_REQUEST_INTERVAL - timeSinceLastRequest),
      );
    }

    lastRequestTime = Date.now();

    // Execute the request (token handled by axios interceptor)
    return await requestFn();
  } catch (error) {
    // Special handling for rate limiting (429) errors
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers?.["retry-after"] || 5;
      const waitTime = parseInt(retryAfter, 10) * 1000;

      log.debug(`Rate limit exceeded. Retrying after ${waitTime}ms`);

      await new Promise((resolve) => setTimeout(resolve, waitTime));
      return enhancedRequest(requestFn);
    }

    throw error;
  }
};

/**
 * Creates an API action creator with standardized configuration
 * @param {string} endpoint - API endpoint path
 * @param {string} method - HTTP method (post, get, put, delete)
 * @returns {Function} - Configured action creator
 */
const createAction = (endpoint, method = "post") => {
  return (data = {}) =>
    enhancedRequest(() =>
      client[method](endpoint, data, { timeout: REQUEST_TIMEOUT }),
    );
};

/**
 * Creates an API action creator for file uploads
 * @param {string} endpoint - API endpoint path
 * @returns {Function} - Configured file upload action creator
 */
const createFileUploadAction = (endpoint) => {
  return (formData) =>
    enhancedRequest(() =>
      client.post(endpoint, formData, {
        timeout: REQUEST_TIMEOUT,
        headers: { "Content-Type": "multipart/form-data" },
      }),
    );
};

// Simplified action creators using the factory functions
export const getSliderlist = createAction("Sliderdata");
export const getSliderOrderlist = createAction("/SliderOrderdata");
export const enabledSlider = createAction("/SliderEnable");
export const diabledSlider = createAction("/SliderDisable");
export const toggleSliderStatus = createAction("/change_slider_status");
export const reorderSlider = createAction("/reorder_slider");

// File upload actions
export const addeditSlider = createFileUploadAction("add_edit_slider");
export const deleteSlider = createAction("/delete_slider");

// Home join section
export const addeditJoin = createAction("/add_edit_homejoin");
export const getJoinlist = createAction("/homejoindata");
export const getJoinOrderlist = createAction("/homejoindataorder");

// Home mission section
export const addeditMission = createAction("/add_edit_homemission");
export const getMissionlist = createAction("/homemissiondata");
export const getMissionOrderlist = createAction("/homemissiondata_order");

// Home news section
export const addeditHomeNews = createAction("/add_edit_homenews");
export const getHomeNewslist = createAction("/homenewsdata");
export const getHomeNewsOrderlist = createAction("/homenewsdata_order");

// News section
export const addeditNews = createAction("/add_edit_news");
export const getNewslist = createAction("/newsdata");
export const getNewsOrderlist = createAction("/newsdata_order");

// Home involve section
export const addeditHomeInvolve = createAction("/add_edit_homeinvole");
export const getHomeInvolvelist = createAction("/homeinvoledata");
export const getHomeInvolveOrderlist = createAction("/homeinvoledata_order");

// Involve section
export const addeditInvolve = createAction("/add_edit_getinvolve");
export const getInvolvelist = createAction("/getinvolvedata");
export const getInvolveOrderlist = createAction("/getinvolvedata_order");
export const reorderGetInvolve = createAction("/reorder_getinvolve");
export const deleteInvolve = createAction("/delete_getinvolve");
export const changeGetInvolveStatus = createAction("/change_getinvolve_status");

// Export a generic action creator factory for use in other modules
export { createAction, createFileUploadAction };
